import React, { useEffect, useState, useRef } from "react";
import CarouselNavigation from "@arctheme-components/blocks/NavigationElements/CarouselNavigation";
import { Image } from "@arctheme-components/elements/Image/Image";
import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";

declare var Flickity: any;

export const ThumbnailLayout = ({ settings, images }) => {
  const { globals, section } = settings;
  const thumbnailPosition: any = "bottom";
  const mainCarouselRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(images[0]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const resizeTimeout: any = useRef(null);

  const useNumbers = false;
  const isMobile = useMobileBreakpoint(section.mobile_breakpoint);
  const showNavigation = isMobile ? section.show_navigation_arrows_mobile : section.show_navigation_arrows;
  const showIndicators = isMobile ? section.show_indicators_mobile : section.show_indicators;
  const showThumbnails = (isMobile ? section.show_thumbnails_mobile : section.show_thumbnails) || false;

  function handleThumbnailClick(image, index) {
    setSelectedImage(image);
    setCurrentIndex(index);

    const flickityMain = Flickity.data(".product-gallery .carousel.carousel-main");
    const flickityNav = Flickity.data(".product-gallery .carousel.carousel-nav");

    flickityMain.select(index);
    flickityNav?.select(index);
  }

  function handlePreviousImage() {
    const prevIndex = currentIndex - 1;

    if (prevIndex >= 0) {
      handleThumbnailClick(images[prevIndex], prevIndex);
    }
  }

  function handleVariantSwitch(event) {
    const { image, index } = event.detail;
    // Get all image elements inside .product-gallery
    const galleryImages = document.querySelectorAll('.product-gallery img');

    // Loop through each image
    galleryImages.forEach(function (img: any) {
      // Check if the image's src contains the target image path
      if (img.src.includes(image)) {
        // Get the parent element of the image
        const parentElement: any = img.parentElement;

        // Get the 'image-id' attribute from the parent element
        const imageId = parentElement.getAttribute('image-id');
        handleThumbnailClick(image, imageId);
        // Log or process the image ID
      }
    });
  }

  function handleCarouselChanged(event) {
    setCurrentIndex(event.detail.index);
  }

  function handleNextImage() {
    const nextIndex = currentIndex + 1;

    if (nextIndex < images.length) {
      handleThumbnailClick(images[nextIndex], nextIndex);
    }
  }

  useEffect(() => {
    const handleResize = () => {
      if (resizeTimeout.current) {
        clearTimeout(resizeTimeout.current);
      }

      resizeTimeout.current = setTimeout(() => {
        if (mainCarouselRef.current) {
          if (typeof Flickity !== "undefined" && mainCarouselRef.current) {
            if (mainCarouselRef.current) {
              const flickityMain = Flickity.data(".product-gallery .carousel.carousel-main");

              if (flickityMain) {
                flickityMain.resize();

              } else {
                let flickityNav = new Flickity(
                  ".product-gallery .carousel.carousel-main",
                  {
                    imagesLoaded: true,
                    wrapAround: true,
                    contain: true,
                    pageDots: false,
                    prevNextButtons: false,
                  },
                );
              }

              let flickityNav = Flickity.data(".product-gallery .carousel.carousel-nav");
              if (flickityNav) {

                flickityNav.resize();
              } else {

                let flickityNav = new Flickity(
                  ".product-gallery .carousel.carousel-nav",
                  {
                    imagesLoaded: true,
                    contain: true,
                    wrapAround: false,
                    draggable: false,
                    pageDots: false,
                    prevNextButtons: false,
                    asNavFor: ".product-gallery .carousel-main",
                  },
                );
              }
            }
          }
        }
      }, 300); // Adjust delay as needed (300ms is typical for debounce)
    };
    window.addEventListener("onCarouselChange", handleCarouselChanged);
    window.addEventListener("shopify:section:load", handleResize);
    window.addEventListener("resize", handleResize);
    window.addEventListener("load", handleResize);
    window.addEventListener("variantImageSwitch", handleVariantSwitch);

    // Clean up the event listener on unmount
    return () => {
      window.removeEventListener("onCarouselChange", handleCarouselChanged);
      window.removeEventListener("shopify:section:load", handleResize);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("load", handleResize);
      window.removeEventListener("variantImageSwitch", handleVariantSwitch);
    };
  }, [mainCarouselRef]);

  return (
    <div
      className={`min-h-[375px] lg:min-h-[700px] product-image-gallery relative ${thumbnailPosition === "bottom" ? "flex flex-col" : "flex flex-row"}`}
    >
      {(showNavigation && images.length > 1) && (
        <div className={"banner-carousel__controls z-[5]"}>
          <CarouselNavigation
            shiftPageDots={false}
            carouselRef={mainCarouselRef}
            spread={true}
            onPrevious={() => handlePreviousImage()}
            onNext={() => handleNextImage()}
            icons={globals?.carousel_icons}
            size={globals?.carousel_icons?.carousel_navigation_arrow_size}
          />
        </div>
      )}

      {/* Thumbnails on the left or above the main image */}
      {images.length > 1 && showThumbnails && (thumbnailPosition === "left" || thumbnailPosition === "top") && (
        <div
          className={`thumbnail-list ${thumbnailPosition === "left" ? "mr-4" : "mb-4"} flex ${thumbnailPosition === "left" ? "flex-col" : "flex-row"}`}
        >
          {images.map((image, index) => (
            <div
              key={index}
              className={`thumbnail-item ${selectedImage === image ? "selected" : ""}`}
              onClick={() => handleThumbnailClick(image, index)}
            >
              <Image imageUrl={image.url} alt={image.alt} />
            </div>
          ))}
        </div>
      )}

      <div
        className="carousel carousel-main"
        {...(true && {
          "x-data": "extended_carousel",
          "x-init":
            "$nextTick(() => { initialise(true, false, false, false, false, 'gallery') })",
          "x-ref": "carousel",
        })}
        data-flickity='{"pageDots": true, "prevNextButtons": false}'
        ref={mainCarouselRef}
      >
        {images.map((image, index) => (
          <div
            key={index}
            className={`carousel-cell ${selectedImage === image ? "selected" : ""}`}
            image-id={index}
            onClick={() => handleThumbnailClick(image, index)}
          >
            <Image imageUrl={image.url} alt={image.alt}  isLazy={index < 1 ? settings.section.is_lazy_load : true}/>
          </div>
        ))}
      </div>

      {showIndicators && useNumbers && images.length > 1 &&
        <div className="text-center">
          {currentIndex + 1} / {images.length}
        </div>
      }

      {/* Thumbnails on the right or below the main image */}
      {images.length > 1 && showThumbnails && (thumbnailPosition === "right" || thumbnailPosition === "bottom") && (
        <div
          className="carousel carousel-nav mt-16"
          data-flickity='{ "asNavFor": ".carousel-main", "contain": true, "pageDots": false, "prevNextButtons": false  }'
        >
          {images.map((image, index) => (
            <div
              key={index}
              className={`thumbnail-item carousel-cell ${selectedImage === image ? "selected" : ""}`}
              onClick={() => handleThumbnailClick(image, index)}
            >
              <Image imageUrl={image.thumbnail} alt={image.alt}  isLazy={index < 1 ? settings.section.is_lazy_load : true}/>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
